// Shared helpers for the optional Player Statistics feature.

// Returns the stat "rows" for a team in a match.
// - If the team has 1+ named players, return those players.
// - If the team has no players, treat the team itself as a single player.
export function getTeamPlayers(participant) {
  if (!participant) return [];
  const players = Array.isArray(participant.players)
    ? participant.players.filter((p) => (p?.name || "").trim())
    : [];
  if (players.length >= 1) {
    return players.map((p) => ({ id: p.id, name: p.name, isTeamItself: false }));
  }
  return [{ id: participant.id, name: participant.name, isTeamItself: true }];
}

// Assists are shown only when a team has 2+ players (a lone player can't assist themselves).
export function teamShowsAssists(participant) {
  const players = Array.isArray(participant?.players)
    ? participant.players.filter((p) => (p?.name || "").trim())
    : [];
  return players.length >= 2;
}

// Build the initial editable rows for one side of a match, pre-filling from
// any previously-saved stats for that match.
export function initStatRows(participant, existingEntries) {
  const rows = getTeamPlayers(participant);
  return rows.map((r) => {
    const prev = (existingEntries || []).find((e) => e.playerId === r.id);
    return {
      playerId: r.id,
      playerName: r.name,
      teamId: participant.id,
      teamName: participant.name,
      goals: prev ? Number(prev.goals) || 0 : 0,
      assists: prev ? Number(prev.assists) || 0 : 0,
    };
  });
}

export function sumGoals(rows) {
  return (rows || []).reduce((acc, r) => acc + (Number(r.goals) || 0), 0);
}

// Aggregate tournament-wide player totals from tournament.playerStats
// (a dict keyed by matchId -> array of stat entries).
export function computePlayerStats(tournament) {
  const dict = (tournament && tournament.playerStats) || {};
  const agg = {};
  Object.values(dict).forEach((entries) => {
    (entries || []).forEach((e) => {
      if (!e || !e.playerId) return;
      if (!agg[e.playerId]) {
        agg[e.playerId] = {
          playerId: e.playerId,
          playerName: e.playerName,
          teamId: e.teamId,
          teamName: e.teamName,
          goals: 0,
          assists: 0,
        };
      }
      agg[e.playerId].goals += Number(e.goals) || 0;
      agg[e.playerId].assists += Number(e.assists) || 0;
    });
  });
  const list = Object.values(agg);
  const scorers = [...list].sort((a, b) => b.goals - a.goals || b.assists - a.assists);
  const assisters = [...list].sort((a, b) => b.assists - a.assists || b.goals - a.goals);
  const combined = [...list].sort(
    (a, b) => (b.goals + b.assists) - (a.goals + a.assists) || b.goals - a.goals
  );
  return {
    list,
    scorers,
    assisters,
    combined,
    topScorer: scorers[0] && scorers[0].goals > 0 ? scorers[0] : null,
    topAssister: assisters[0] && assisters[0].assists > 0 ? assisters[0] : null,
    topGA: combined[0] && (combined[0].goals + combined[0].assists) > 0 ? combined[0] : null,
    hasAny: list.some((p) => p.goals > 0 || p.assists > 0),
  };
}

// Map playerId -> aggregated totals (for showing next to names).
export function playerTotalsMap(tournament) {
  const { list } = computePlayerStats(tournament);
  const m = {};
  list.forEach((p) => {
    m[p.playerId] = p;
  });
  return m;
}
